import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { bannerService } from '../../services/bannerService';
import { uploadService } from '../../services/uploadService';
import { RestaurantsService } from '../../services/restaurantsService';
import { BannerResponse, CreateBannerRequest, UpdateBannerRequest } from '../../types/banner';
import { RestaurantResponse, RestaurantDetailsResponse } from '../../types/restaurants';
import './Page.css';

const restaurantsService = new RestaurantsService();

const Banners: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState<BannerResponse[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerResponse | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageId, setImageId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [priority, setPriority] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  // Metadata state
  const [bannerAction, setBannerAction] = useState<'NONE' | 'ACTION_OPEN_RESTAURANT' | 'ACTION_OPEN_RESTAURANT_CATEGORY' | 'ACTION_OPEN_URL' | 'ACTION_OPEN_IMAGE'>('ACTION_OPEN_RESTAURANT');
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [urlValue, setUrlValue] = useState('');
  const [imageUrlValue, setImageUrlValue] = useState('');

  // Restaurant search state
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurants, setRestaurants] = useState<RestaurantResponse[]>([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [restaurantDetails, setRestaurantDetails] = useState<RestaurantDetailsResponse | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Drag and drop state
  const [reorderedBanners, setReorderedBanners] = useState<BannerResponse[]>([]);
  const [hasReordered, setHasReordered] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    // Initialize reordered banners when banners are loaded
    setReorderedBanners(banners);
    setHasReordered(false);
  }, [banners]);

  const fetchBanners = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await bannerService.getAllBanners();

      if (response.success && response.data) {
        setBanners(response.data);
      } else {
        setError(response.error || 'Failed to fetch banners');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBanner(null);
    setTitle('');
    setDescription('');
    setImageId('');
    setImageUrl('');
    setImagePreviewUrl('');
    setPriority(banners.length.toString());
    setIsActive(true);
    setImageFile(null);
    setBannerAction('ACTION_OPEN_RESTAURANT');
    setSelectedRestaurantId(null);
    setSelectedCategoryId(null);
    setUrlValue('');
    setImageUrlValue('');
    setRestaurantSearch('');
    setRestaurants([]);
    setRestaurantDetails(null);
    setShowModal(true);
  };

  const openEditModal = (banner: BannerResponse) => {
    setEditingBanner(banner);
    setTitle(banner.title);
    setDescription(banner.description);
    setImageId(banner.imageId);
    setImageUrl(banner.imageUrl);
    setImagePreviewUrl('');
    setPriority(banner.priority.toString());
    setIsActive(banner.isActive);
    setImageFile(null);

    // Parse metadata
    const action = banner.metadata['BANNER_ACTION'] as 'NONE' | 'ACTION_OPEN_RESTAURANT' | 'ACTION_OPEN_RESTAURANT_CATEGORY' | 'ACTION_OPEN_URL' | 'ACTION_OPEN_IMAGE' | undefined;
    if (action) {
      setBannerAction(action);

      if (action === 'ACTION_OPEN_RESTAURANT') {
        const restaurantId = banner.metadata['RESTAURANT_ID'];
        setSelectedRestaurantId(restaurantId ? parseInt(restaurantId) : null);
      } else if (action === 'ACTION_OPEN_RESTAURANT_CATEGORY') {
        const restaurantId = banner.metadata['RESTAURANT_ID'];
        const categoryId = banner.metadata['CATEGORY_ID'];
        setSelectedRestaurantId(restaurantId ? parseInt(restaurantId) : null);
        setSelectedCategoryId(categoryId ? parseInt(categoryId) : null);

        // Load restaurant details for categories
        if (restaurantId) {
          loadRestaurantDetails(parseInt(restaurantId));
        }
      } else if (action === 'ACTION_OPEN_URL') {
        setUrlValue(banner.metadata['URL'] || '');
      } else if (action === 'ACTION_OPEN_IMAGE') {
        setImageUrlValue(banner.metadata['IMAGE_URL'] || '');
      }
    } else {
      setBannerAction('ACTION_OPEN_RESTAURANT');
      setSelectedRestaurantId(null);
      setSelectedCategoryId(null);
      setUrlValue('');
      setImageUrlValue('');
    }

    setRestaurantSearch('');
    setRestaurants([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setError('');
  };

  const searchRestaurants = async (query: string) => {
    if (query.length < 2) {
      setRestaurants([]);
      return;
    }

    setLoadingRestaurants(true);

    try {
      const response = await restaurantsService.getRestaurants({
        searchQuery: query,
        page: 0,
        pageSize: 20,
        applicationStatus: null,
      });

      if (response.success && response.data) {
        setRestaurants(response.data);
      }
    } catch (error) {
      console.error('Failed to search restaurants:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const loadRestaurantDetails = async (restaurantId: number) => {
    setLoadingCategories(true);

    try {
      const response = await restaurantsService.getRestaurantDetails(restaurantId);

      if (response.success && response.data) {
        setRestaurantDetails(response.data);
      }
    } catch (error) {
      console.error('Failed to load restaurant details:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleRestaurantSelect = async (restaurantId: number) => {
    setSelectedRestaurantId(restaurantId);
    setRestaurantSearch('');
    setRestaurants([]);

    if (bannerAction === 'ACTION_OPEN_RESTAURANT_CATEGORY') {
      await loadRestaurantDetails(restaurantId);
    }
  };

  const handleActionChange = (action: 'NONE' | 'ACTION_OPEN_RESTAURANT' | 'ACTION_OPEN_RESTAURANT_CATEGORY' | 'ACTION_OPEN_URL' | 'ACTION_OPEN_IMAGE') => {
    setBannerAction(action);
    setSelectedRestaurantId(null);
    setSelectedCategoryId(null);
    setUrlValue('');
    setImageUrlValue('');
    setRestaurantDetails(null);
    setRestaurants([]);
    setRestaurantSearch('');
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('banners.invalidImageType'));
      return;
    }

    // Validate file size (10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('banners.imageTooLarge'));
      return;
    }

    setError('');

    try {
      // Store the file for later upload
      setImageFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);

      // Clear existing image ID if editing
      if (!editingBanner) {
        setImageId('');
      }
    } catch (error) {
      setError('Failed to load image preview');
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!title.trim()) {
      setError(t('banners.titleRequired'));
      return;
    }

    // Check if we have an image (either existing imageId or new imageFile)
    if (!imageId && !imageFile) {
      setError(t('banners.imageRequired'));
      return;
    }

    const priorityNum = parseInt(priority);
    if (isNaN(priorityNum) || priorityNum < 0) {
      setError('Please enter a valid priority (0 or greater)');
      return;
    }

    // Upload new image if selected
    let finalImageId = imageId;
    if (imageFile) {
      setModalLoading(true);

      try {
        // Compress image using browser-image-compression
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg' as const,
        };
        const compressedFile = await imageCompression(imageFile, options);

        // Upload image
        const uploadResponse = await uploadService.uploadImage(compressedFile);

        if (uploadResponse.success && uploadResponse.data) {
          finalImageId = uploadResponse.data.id;
        } else {
          setError(uploadResponse.error || t('banners.uploadFailed'));
          setModalLoading(false);
          return;
        }
      } catch (error) {
        setError(t('banners.uploadFailed'));
        setModalLoading(false);
        return;
      }
    }

    // Build metadata object based on action
    const metadata: Record<string, string> = {
      BANNER_ACTION: bannerAction,
    };

    if (bannerAction === 'ACTION_OPEN_RESTAURANT') {
      if (!selectedRestaurantId) {
        setError('Please select a restaurant');
        return;
      }
      metadata.RESTAURANT_ID = selectedRestaurantId.toString();
    } else if (bannerAction === 'ACTION_OPEN_RESTAURANT_CATEGORY') {
      if (!selectedRestaurantId) {
        setError('Please select a restaurant');
        return;
      }
      if (!selectedCategoryId) {
        setError('Please select a category');
        return;
      }
      metadata.RESTAURANT_ID = selectedRestaurantId.toString();
      metadata.CATEGORY_ID = selectedCategoryId.toString();
    } else if (bannerAction === 'ACTION_OPEN_URL') {
      if (!urlValue.trim()) {
        setError('Please enter a URL');
        return;
      }
      metadata.URL = urlValue.trim();
    } else if (bannerAction === 'ACTION_OPEN_IMAGE') {
      if (!imageUrlValue.trim()) {
        setError('Please enter an image URL');
        return;
      }
      metadata.IMAGE_URL = imageUrlValue.trim();
    }

    if (!modalLoading) {
      setModalLoading(true);
    }

    try {
      if (editingBanner) {
        // Update banner
        const request: UpdateBannerRequest = {
          title: title.trim(),
          description: description.trim(),
          imageId: finalImageId,
          priority: priorityNum,
          isActive,
          metadata,
        };

        const response = await bannerService.updateBanner(editingBanner.id, request);

        if (response.success) {
          setSuccessMessage(t('banners.bannerUpdated'));
          await fetchBanners();
          closeModal();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to update banner');
        }
      } else {
        // Create banner
        const request: CreateBannerRequest = {
          title: title.trim(),
          description: description.trim(),
          imageId: finalImageId,
          priority: priorityNum,
          isActive,
          metadata,
        };

        const response = await bannerService.createBanner(request);

        if (response.success) {
          setSuccessMessage(t('banners.bannerCreated'));
          await fetchBanners();
          closeModal();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to create banner');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (bannerId: number) => {
    if (!window.confirm(t('banners.confirmDelete'))) {
      return;
    }

    setError('');

    try {
      const response = await bannerService.deleteBanner(bannerId);

      if (response.success) {
        setSuccessMessage(t('banners.bannerDeleted'));
        await fetchBanners();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete banner');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newBanners = [...reorderedBanners];
    const draggedItem = newBanners[draggedIndex];

    // Remove dragged item
    newBanners.splice(draggedIndex, 1);

    // Insert at new position
    newBanners.splice(index, 0, draggedItem);

    setReorderedBanners(newBanners);
    setDraggedIndex(index);
    setHasReordered(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const savePriorities = async () => {
    setModalLoading(true);
    setError('');

    try {
      const request = {
        banners: reorderedBanners.map((banner, index) => ({
          bannerId: banner.id,
          priority: index,
        })),
      };

      const response = await bannerService.updateBannerPriorities(request);

      if (response.success) {
        setSuccessMessage(t('banners.prioritiesUpdated'));
        setHasReordered(false);
        await fetchBanners();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update priorities');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const cancelReorder = () => {
    setReorderedBanners(banners);
    setHasReordered(false);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{t('banners.title')}</h1>
          <p>{t('banners.subtitle')}</p>
        </div>
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('banners.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="action-button secondary"
          >
            ← {t('banners.backToSettings')}
          </button>
          <div>
            <h1>{t('banners.title')}</h1>
            <p>{t('banners.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            {successMessage}
          </div>
        )}

        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem' }}>
          <button
            type="button"
            onClick={openCreateModal}
            style={{
              padding: '0.6rem 1.2rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
          >
            + {t('banners.createNewBanner')}
          </button>

          {hasReordered && (
            <>
              <button
                type="button"
                onClick={savePriorities}
                disabled={modalLoading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: modalLoading ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: modalLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#0056b3')}
                onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#007bff')}
              >
                {modalLoading ? t('banners.savingPriorities') : t('banners.saveReorder')}
              </button>
              <button
                type="button"
                onClick={cancelReorder}
                disabled={modalLoading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: modalLoading ? '#e0e0e0' : '#f8f9fa',
                  color: modalLoading ? '#999' : '#333',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: modalLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#e2e6ea')}
                onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                {t('banners.cancel')}
              </button>
            </>
          )}
        </div>

        {banners.length === 0 ? (
          <div className="detail-card full-width">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <p>{t('banners.noBanners')}</p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {reorderedBanners.map((banner, index) => (
              <div
                key={banner.id}
                className="detail-card full-width"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: 'grab',
                  transition: 'background-color 0.2s',
                  backgroundColor: draggedIndex === index ? '#f0f8ff' : 'white',
                }}
              >
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                  {/* Banner Image */}
                  <div
                    style={{
                      width: '200px',
                      height: '120px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {banner.imageUrl ? (
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#999' }}>{t('banners.noImage')}</span>
                    )}
                  </div>

                  {/* Banner Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0 }}>{banner.title}</h3>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: banner.isActive ? '#d4edda' : '#f8d7da',
                          color: banner.isActive ? '#155724' : '#721c24',
                          border: `1px solid ${banner.isActive ? '#c3e6cb' : '#f5c6cb'}`,
                        }}
                      >
                        {banner.isActive ? t('banners.active') : t('banners.inactive')}
                      </span>
                    </div>
                    <p style={{ color: '#666', marginBottom: '0.75rem' }}>{banner.description}</p>

                    <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.75rem' }}>
                      <div>
                        <strong>{t('banners.priority')}:</strong> {index}
                      </div>
                    </div>

                    {banner.metadata?.BANNER_ACTION && (
                      <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        <strong>Action:</strong>{' '}
                        <span style={{ backgroundColor: '#e7f3ff', padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                          {banner.metadata.BANNER_ACTION === 'NONE' && 'None'}
                          {banner.metadata.BANNER_ACTION === 'ACTION_OPEN_RESTAURANT' && 'Open Restaurant'}
                          {banner.metadata.BANNER_ACTION === 'ACTION_OPEN_RESTAURANT_CATEGORY' && 'Open Restaurant Category'}
                          {banner.metadata.BANNER_ACTION === 'ACTION_OPEN_URL' && 'Open URL'}
                          {banner.metadata.BANNER_ACTION === 'ACTION_OPEN_IMAGE' && 'Open Image'}
                        </span>
                        {banner.metadata.RESTAURANT_ID && (
                          <span style={{ marginLeft: '0.5rem' }}>
                            | Restaurant ID: <code>{banner.metadata.RESTAURANT_ID}</code>
                          </span>
                        )}
                        {banner.metadata.CATEGORY_ID && (
                          <span style={{ marginLeft: '0.5rem' }}>
                            | Category ID: <code>{banner.metadata.CATEGORY_ID}</code>
                          </span>
                        )}
                        {banner.metadata.URL && (
                          <span style={{ marginLeft: '0.5rem' }}>
                            | URL: <code style={{ wordBreak: 'break-all' }}>{banner.metadata.URL}</code>
                          </span>
                        )}
                        {banner.metadata.IMAGE_URL && (
                          <span style={{ marginLeft: '0.5rem' }}>
                            | Image URL: <code style={{ wordBreak: 'break-all' }}>{banner.metadata.IMAGE_URL}</code>
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => openEditModal(banner)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(banner.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
            overflowY: 'auto',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '95vh',
              overflow: 'auto',
              padding: '2.5rem',
              margin: '1rem auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '1.5rem' }}>
              {editingBanner ? t('banners.editBanner') : t('banners.createBanner')}
            </h2>

            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('banners.bannerTitle')} *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('banners.bannerTitlePlaceholder')}
                  className="search-input"
                  required
                  disabled={modalLoading}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('banners.bannerDescription')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="search-input"
                  disabled={modalLoading}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Is Active */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={modalLoading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 500 }}>{t('banners.isActive')}</span>
                </label>
                <small style={{ display: 'block', marginTop: '0.25rem', marginLeft: '26px', color: '#666' }}>
                  {t('banners.isActiveHint')}
                </small>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('banners.bannerImage')} *
                </label>
                <small style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
                  {t('banners.bannerImageHint')}
                </small>

                {(imagePreviewUrl || imageUrl) && (
                  <div
                    style={{
                      marginBottom: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxWidth: '400px',
                    }}
                  >
                    <img
                      src={imagePreviewUrl || imageUrl}
                      alt="Preview"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <label
                    htmlFor="banner-image-upload"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: modalLoading ? '#ccc' : '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: modalLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'background-color 0.2s',
                      display: 'inline-block',
                    }}
                    onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#138496')}
                    onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#17a2b8')}
                  >
                    {imagePreviewUrl || imageUrl
                      ? t('banners.changeImage')
                      : t('banners.uploadImage')}
                  </label>
                  <input
                    id="banner-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    disabled={modalLoading}
                    style={{ display: 'none' }}
                  />

                  {(imagePreviewUrl || imageUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageId('');
                        setImageUrl('');
                        setImagePreviewUrl('');
                        setImageFile(null);
                      }}
                      disabled={modalLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: modalLoading ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: modalLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#c82333')}
                      onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#dc3545')}
                    >
                      {t('banners.removeImage')}
                    </button>
                  )}
                </div>
              </div>

              {/* Banner Action */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Banner Action *
                </label>
                <select
                  value={bannerAction}
                  onChange={(e) => handleActionChange(e.target.value as any)}
                  className="search-input"
                  disabled={modalLoading}
                >
                  <option value="ACTION_OPEN_RESTAURANT">Open Restaurant</option>
                  <option value="ACTION_OPEN_RESTAURANT_CATEGORY">Open Restaurant Category</option>
                  <option value="ACTION_OPEN_URL">Open URL</option>
                  <option value="ACTION_OPEN_IMAGE">Open Image</option>
                  <option value="NONE">None</option>
                </select>
              </div>

              {/* Restaurant Selection (for OPEN_RESTAURANT and OPEN_RESTAURANT_CATEGORY) */}
              {(bannerAction === 'ACTION_OPEN_RESTAURANT' || bannerAction === 'ACTION_OPEN_RESTAURANT_CATEGORY') && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Restaurant *
                  </label>
                  {selectedRestaurantId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ padding: '0.5rem', backgroundColor: '#e9ecef', borderRadius: '4px', flex: 1 }}>
                        Restaurant ID: {selectedRestaurantId}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRestaurantId(null);
                          setSelectedCategoryId(null);
                          setRestaurantDetails(null);
                        }}
                        disabled={modalLoading}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={restaurantSearch}
                        onChange={(e) => {
                          setRestaurantSearch(e.target.value);
                          searchRestaurants(e.target.value);
                        }}
                        placeholder="Search restaurant by name..."
                        className="search-input"
                        disabled={modalLoading}
                      />
                      {loadingRestaurants && (
                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                          Searching...
                        </small>
                      )}
                      {restaurants.length > 0 && (
                        <div
                          style={{
                            marginTop: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                        >
                          {restaurants.map((restaurant) => (
                            <div
                              key={restaurant.id}
                              onClick={() => handleRestaurantSelect(restaurant.id)}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                transition: 'background-color 0.2s',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div style={{ fontWeight: 500 }}>{restaurant.name}</div>
                              <small style={{ color: '#666' }}>ID: {restaurant.id}</small>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Category Selection (for OPEN_RESTAURANT_CATEGORY only) */}
              {bannerAction === 'ACTION_OPEN_RESTAURANT_CATEGORY' && selectedRestaurantId && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Category *
                  </label>
                  {loadingCategories ? (
                    <div style={{ padding: '0.5rem', color: '#666' }}>Loading categories...</div>
                  ) : restaurantDetails?.categoriesWithFoods.length ? (
                    <select
                      value={selectedCategoryId || ''}
                      onChange={(e) => setSelectedCategoryId(parseInt(e.target.value))}
                      className="search-input"
                      disabled={modalLoading}
                    >
                      <option value="">Select a category...</option>
                      {restaurantDetails.categoriesWithFoods.map((category) => (
                        <option key={category.categoryId} value={category.categoryId}>
                          {category.categoryName} (ID: {category.categoryId})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ padding: '0.5rem', color: '#999' }}>No categories available for this restaurant</div>
                  )}
                </div>
              )}

              {/* URL Input (for OPEN_URL only) */}
              {bannerAction === 'ACTION_OPEN_URL' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    URL *
                  </label>
                  <input
                    type="url"
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    placeholder="https://example.com"
                    className="search-input"
                    disabled={modalLoading}
                  />
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                    Enter the full URL including https://
                  </small>
                </div>
              )}

              {/* Image URL Input (for OPEN_IMAGE only) */}
              {bannerAction === 'ACTION_OPEN_IMAGE' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Image URL *
                  </label>
                  <input
                    type="url"
                    value={imageUrlValue}
                    onChange={(e) => setImageUrlValue(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="search-input"
                    disabled={modalLoading}
                  />
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                    Enter the full image URL including https://
                  </small>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="submit"
                  disabled={modalLoading}
                  style={{
                    padding: '0.6rem 1.5rem',
                    backgroundColor: modalLoading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: modalLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#218838')}
                  onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#28a745')}
                >
                  {modalLoading
                    ? (imageFile ? t('banners.uploadingImage') + '...' : t('banners.saving'))
                    : editingBanner
                    ? t('banners.updateBanner')
                    : t('banners.createBanner')}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={modalLoading}
                  style={{
                    padding: '0.6rem 1.5rem',
                    backgroundColor: modalLoading ? '#e0e0e0' : '#f8f9fa',
                    color: modalLoading ? '#999' : '#333',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: modalLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#e2e6ea')}
                  onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                >
                  {t('banners.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;
